import { inject, Injectable, signal } from '@angular/core';
import { B2BInput } from 'src/app/interfaces/b2b-input';
import { CustomersProviderService } from 'src/app/providers/customers/customers.provider.service';
import { LoadingService } from '../loading/loading.service';

/**
 * Owns the retailer iframe key for the current session.
 *
 * A session carries one of two identities, never both:
 *   - retailer/iframe sessions have `retailerIframeID` in the b2b payload;
 *   - B2B (portal) sessions have `customerToken` instead, and the iframe key has to be fetched
 *     with it via `GET /api/customers/{ringBuilderID}/iframeid`.
 *
 * This lived in ShareService, which meant anything else needing the key either could not reach it
 * or silently used the payload field and got `undefined` in a B2B session.
 *
 * Deliberately does NOT inject CheckoutService: CheckoutService injects this, so taking the
 * dependency back would make a DI cycle. Callers pass the b2b payload in instead.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private customersProvider = inject(CustomersProviderService);
  private loadingService = inject(LoadingService);

  retailerIframeID = signal<string | null>(null);

  private inFlight: Promise<string | null> | null = null;
  private resolvedForToken: string | null = null;

  /**
   * The retailer iframe key for this session, from whichever identity it carries.
   *
   * Memoised per customerToken, so concurrent callers share one request and a later caller (e.g.
   * add-to-cart) awaits the fetch already in flight rather than starting a second one or reading a
   * signal that has not been filled yet.
   */
  async resolveIframeID(b2bData: B2BInput | null): Promise<string | null> {
    if (!b2bData) {
      return null;
    }

    const customerToken = b2bData.customerToken;
    if (!customerToken) {
      const iframeID = b2bData.retailerIframeID ?? null;
      this.retailerIframeID.set(iframeID);
      return iframeID;
    }

    if (this.resolvedForToken === customerToken && this.inFlight) {
      return this.inFlight;
    }

    this.resolvedForToken = customerToken;
    this.loadingService.load('CustomerService::iframeid');
    this.inFlight = this.customersProvider.getIframeID(customerToken)
      .then(iframeID => {
        this.retailerIframeID.set(iframeID);
        return iframeID;
      })
      .finally(() => this.loadingService.done('CustomerService::iframeid'));

    return this.inFlight;
  }
}
